// 변경 - useQuery autoRefetch 옵션 조정

import { useEffect, useState, createContext, useContext } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { Session } from "@supabase/supabase-js";
import { SearchPage } from "@/pages/search";
import { ProfilePreview } from "@/pages/profile-preview";
import { ChatPage } from "@/pages/chat";
import { ChatPage as SavedChatsPage } from "@/pages/chat-page";
import { SettingsPage } from "@/pages/settings";
import { ProfilePage } from "@/pages/profile";
import { PricingPage } from "@/pages/pricing";
import { AuthPage } from "@/pages/auth";
import { TermsPage } from "@/pages/terms";
import { InstantChatPage } from "@/pages/instant-chat";
import { MessagesPage } from "@/pages/messages";
import { supabase } from "@/lib/supabase";
import { getSavedAccount } from "@/lib/saved-accounts-service";
import { ScrollToTop } from "@/components/scroll-to-top";
import { Preloader } from "@/components/ui/preloader";

// 인증 컨텍스트 타입 정의
interface AuthContextType {
  session: Session | null;
  loading: boolean;
}

// 인증 컨텍스트 생성
const AuthContext = createContext<AuthContextType>({
  session: null,
  loading: true,
});

// 인증 컨텍스트 사용 Hook
export const useAuth = () => {
  return useContext(AuthContext);
};

// 비공개 라우트 컴포넌트 - 최적화
function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { session, loading } = useAuth();
  const location = useLocation();

  // 인증 상태 로딩 중 표시 (새로운 로딩 스피너로 변경)
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <Preloader fullScreen />
      </div>
    );
  }

  // 세션 없으면 로그인 페이지로 리다이렉트 (replace 사용)
  if (!session) {
    return <Navigate to="/auth" state={{ from: location.pathname }} replace />;
  }

  // 세션 있으면 자식 컴포넌트 렌더링
  return children;
}

// 메인 라우트 로직 컴포넌트 (Search, Preview, Chat 상태 관리)
function MainRoutes() {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentState, setCurrentState] = useState<
    "search" | "preview" | "chat"
  >("search");
  const [username, setUsername] = useState("");
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const { session } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [lastLocationChange, setLastLocationChange] = useState<number>(
    Date.now()
  );

  // location.state 변경 감지 시 디바운싱 적용
  useEffect(() => {
    const locationState = location.state as {
      username?: string;
      direct?: string;
      profile?: any;
    } | null;

    const usernameFromState = locationState?.username;
    const directMode = locationState?.direct;
    const profileFromState = locationState?.profile;
    const now = Date.now();

    // direct=chat 모드가 설정된 경우 바로 채팅 화면으로 이동
    if (usernameFromState && directMode === "chat" && profileFromState) {
      console.log("직접 채팅 화면으로 이동:", usernameFromState);
      setLastLocationChange(now);
      setUsername(usernameFromState);

      // 프로필 데이터 설정
      setProfileData({
        username: usernameFromState,
        profile: profileFromState,
      });

      // 바로 채팅 상태로 전환
      setCurrentState("chat");

      // state 초기화 (중복 처리 방지)
      navigate(location.pathname, { replace: true, state: null });
      return;
    }

    // 디바운싱: 마지막 변경 이후 500ms 이내면 처리하지 않음
    if (
      usernameFromState &&
      currentState === "search" &&
      now - lastLocationChange > 500
    ) {
      console.log("Navigated with username:", usernameFromState);
      setLastLocationChange(now); // 시간 업데이트
      setUsername(usernameFromState);
      setIsLoading(true);

      // 로컬 스토리지 확인을 먼저 진행하여 빠른 응답 제공
      const checkLocalStorageFirst = async () => {
        try {
          const savedProfile = localStorage.getItem(
            `profile-${usernameFromState}`
          );
          if (savedProfile) {
            console.log("Found profile in localStorage:", usernameFromState);
            const profile = JSON.parse(savedProfile);
            setProfileData({ username: usernameFromState, profile });
            setCurrentState("chat");
            navigate(location.pathname, { replace: true, state: null });
            setIsLoading(false);
            return true;
          }
          return false;
        } catch (error) {
          console.error("Error checking localStorage:", error);
          return false;
        }
      };

      // 로컬 스토리지에서 찾지 못한 경우에만 Supabase 조회
      checkLocalStorageFirst().then((foundInLocal) => {
        if (!foundInLocal) {
          if (session) {
            // 로그인한 사용자만 저장된 계정 확인
            getSavedAccount(usernameFromState)
              .then((savedAccount) => {
                if (savedAccount?.profile_data) {
                  console.log("Found saved account:", savedAccount.username);
                  setProfileData({
                    username: usernameFromState,
                    profile: savedAccount.profile_data,
                  });
                  setCurrentState("chat");
                  // 로컬 스토리지에도 저장
                  localStorage.setItem(
                    `profile-${usernameFromState}`,
                    JSON.stringify(savedAccount.profile_data)
                  );
                } else {
                  console.log(
                    "Account not found, going to preview:",
                    usernameFromState
                  );
                  setCurrentState("preview");
                }
                navigate(location.pathname, { replace: true, state: null });
              })
              .catch((error) => {
                console.error("Error fetching saved account:", error);
                setCurrentState("preview");
                navigate(location.pathname, { replace: true, state: null });
              })
              .finally(() => {
                setIsLoading(false);
              });
          } else {
            // 비로그인 사용자는 항상 preview로
            setCurrentState("preview");
            setIsLoading(false);
            navigate(location.pathname, { replace: true, state: null });
          }
        }
      });
    }
  }, [
    location.state,
    session,
    currentState,
    navigate,
    location.pathname,
    lastLocationChange,
  ]);

  // 검색 시작 핸들러
  const handleSearch = (newUsername: string) => {
    setUsername(newUsername);
    setProfileData(null);
    setCurrentState("preview");
  };

  // 뒤로가기/리셋 핸들러
  const handleBack = () => {
    setUsername("");
    setProfileData(null);
    setCurrentState("search");
  };

  // 채팅 시작 핸들러 (ProfilePreview에서 호출)
  const handleContinue = (profile: any) => {
    if (!username) {
      console.error("Cannot continue without a username.");
      handleBack();
      return;
    }
    const data: ProfileData = { username, profile };
    setProfileData(data);
    setCurrentState("chat");
  };

  // 로딩 상태 표시 - 새로운 로딩 스피너 컴포넌트로 변경
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <Preloader fullScreen />
      </div>
    );
  }

  // 상태에 따른 컴포넌트 렌더링
  if (currentState === "preview" && username) {
    return (
      <ProfilePreview
        username={username}
        onContinue={handleContinue}
        onBack={handleBack}
      />
    );
  }

  if (currentState === "chat" && profileData) {
    return (
      <ChatPage
        username={profileData.username}
        initialProfile={profileData.profile}
        onReset={handleBack}
      />
    );
  }

  // 기본 상태는 검색 페이지
  return <SearchPage onUsernameSubmit={handleSearch} />;
}

// 앱 전체 라우터 설정 컴포넌트 - 최적화
function AppRouter() {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  // 인증 상태 감지 및 초기화 - 성능 최적화
  useEffect(() => {
    // 세션 로딩 디버깅용 타임스탬프
    const sessionStartTime = Date.now();
    console.log(`[${new Date().toISOString()}] 인증 상태 로딩 시작`);

    // 세션 데이터 로드 함수 - 비동기적으로 처리
    async function initializeAuth() {
      try {
        const { data } = await supabase.auth.getSession();
        console.log(
          `[${new Date().toISOString()}] 세션 로드 완료 (${Date.now() - sessionStartTime
          }ms)`
        );
        setSession(data.session);
      } catch (error) {
        console.error("Error getting session:", error);
      } finally {
        // 무조건 로딩 상태 해제
        setLoading(false);
      }
    }

    initializeAuth();

    // 이벤트 리스너 등록 - 메모리 누수 방지를 위한 구독 해제 보장
    const { data: authListener } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        const eventTime = Date.now();
        console.log(
          `[${new Date().toISOString()}] 인증 상태 변경: ${_event} (${eventTime - sessionStartTime
          }ms 경과)`
        );

        // 세션 업데이트만 수행하고, 추가 작업은 하지 않음
        setSession(session);
        if (loading) setLoading(false);
      }
    );

    // 클린업 함수
    return () => {
      authListener?.subscription?.unsubscribe();
      console.log(`[${new Date().toISOString()}] 인증 이벤트 리스너 제거 완료`);
    };
  }, [loading]);

  return (
    <AuthContext.Provider value={{ session, loading }}>
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/auth" element={<AuthPage />} />

          <Route
            path="/chat"
            element={
              <PrivateRoute>
                <SavedChatsPage />
              </PrivateRoute>
            }
          />

          <Route path="/pricing" element={<PricingPage />} />

          <Route
            path="/settings"
            element={
              <PrivateRoute>
                <SettingsPage />
              </PrivateRoute>
            }
          />

          <Route
            path="/profile"
            element={
              <PrivateRoute>
                <ProfilePage />
              </PrivateRoute>
            }
          />

          <Route path="/terms" element={<TermsPage />} />

          {/* 홈 경로를 Search 페이지로 리디렉션 */}
          <Route path="/" element={<Navigate to="/search" replace />} />

          {/* InstantChat 페이지는 direct URL로만 접근 가능하도록 별도 경로 유지 */}
          <Route path="/instant-chat" element={<InstantChatPage />} />

          {/* 메시지 페이지 */}
          <Route path="/messages" element={
            <PrivateRoute>
              <MessagesPage />
            </PrivateRoute>
          } />

          {/* Search and chat flow */}
          <Route path="/search" element={<MainRoutes />} />
        </Routes>
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

// 프로필 데이터 타입 정의
type ProfileData = {
  username: string;
  profile: any;
};

// Router export
export { AppRouter as Router };
